import React, { useState, useRef, useEffect } from 'react';
import { useAIChat } from '../../context/AIChatContext';
import { useTheme } from '../../context/ThemeContext';
import ReactMarkdown from 'react-markdown';
import TaskPreviewCard from './TaskPreviewCard';
import PlanPreviewCard from './PlanPreviewCard';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  TrashIcon,
  ChevronDownIcon,
  StopIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  PhotoIcon,
  LightBulbIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const AIChatPanel = () => {
  const { isDark } = useTheme();
  const {
    isOpen,
    closePanel,
    conversations,
    currentConversation,
    messages,
    sendMessage,
    isLoading,
    isStreaming,
    streamingMessage,
    stopStreaming,
    createConversation,
    loadConversation,
    deleteConversation,
    context,
    error,
    clearError,
    // Task & Plan generation
    generatedTasks,
    generatedPlan,
    isGenerating,
    generateTasksFromConversation,
    generatePlanFromConversation,
    saveTasks,
    savePlan,
    clearGeneratedContent
  } = useAIChat();

  const [inputValue, setInputValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const historyRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (historyRef.current && !historyRef.current.contains(event.target)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading && !isStreaming) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    createConversation();
    setShowHistory(false);
  };

  const handleSelectConversation = (conversation) => {
    loadConversation(conversation.uuid || conversation.id);
    setShowHistory(false);
  };

  const handleDeleteConversation = (e, conversation) => {
    e.stopPropagation();
    deleteConversation(conversation.uuid || conversation.id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const suggestedPrompts = [
    { icon: ClipboardDocumentListIcon, text: 'Create a marketing plan', prompt: 'Help me create a marketing plan for my project' },
    { icon: DocumentTextIcon, text: 'Generate content ideas', prompt: 'Generate 5 content ideas for social media' },
    { icon: PhotoIcon, text: 'Create an image', prompt: 'Generate an image for my marketing campaign' },
    { icon: SparklesIcon, text: 'Analyze campaign', prompt: 'Analyze my current campaign performance and suggest improvements' }
  ];

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      zIndex: 998,
      opacity: isOpen ? 1 : 0,
      transition: 'opacity 0.3s ease'
    },
    panel: {
      position: 'fixed',
      top: 0,
      right: 0,
      width: '420px',
      maxWidth: '100vw',
      height: '100vh',
      backgroundColor: isDark ? '#1f2937' : 'white',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      zIndex: 999,
      display: 'flex',
      flexDirection: 'column',
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease'
    },
    header: {
      padding: '1rem 1.25rem',
      borderBottom: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: isDark ? '#1f2937' : 'white',
      color: isDark ? '#f9fafb' : 'var(--gray-900)'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    headerIcon: {
      width: '1.5rem',
      height: '1.5rem',
      color: 'var(--primary-600)'
    },
    headerTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      margin: 0,
      color: 'var(--gray-900)'
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    iconButton: {
      background: 'var(--gray-100)',
      border: 'none',
      borderRadius: '0.5rem',
      padding: '0.5rem',
      cursor: 'pointer',
      color: 'var(--gray-600)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    historyDropdown: {
      position: 'relative',
      ref: historyRef
    },
    historyMenu: {
      position: 'absolute',
      top: '100%',
      right: 0,
      marginTop: '0.5rem',
      width: '300px',
      maxHeight: '400px',
      overflowY: 'auto',
      backgroundColor: isDark ? '#1f2937' : 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
      zIndex: 1000,
      border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`
    },
    historyHeader: {
      padding: '0.75rem 1rem',
      borderBottom: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'sticky',
      top: 0,
      backgroundColor: isDark ? '#1f2937' : 'white'
    },
    historyTitle: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: 'var(--gray-900)'
    },
    newChatButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.375rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      color: 'var(--primary-600)',
      backgroundColor: 'var(--primary-50)',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    historyItem: {
      padding: '0.75rem 1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      borderBottom: '1px solid var(--gray-100)',
      transition: 'background-color 0.2s'
    },
    historyItemActive: {
      backgroundColor: 'var(--primary-50)'
    },
    historyItemTitle: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: 'var(--gray-900)',
      marginBottom: '0.125rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '200px'
    },
    historyItemDate: {
      fontSize: '0.75rem',
      color: 'var(--gray-500)'
    },
    deleteButton: {
      padding: '0.25rem',
      backgroundColor: 'transparent',
      border: 'none',
      color: 'var(--gray-400)',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    contextBanner: {
      padding: '0.625rem 1rem',
      backgroundColor: 'var(--primary-50)',
      borderBottom: '1px solid var(--primary-100)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.8125rem',
      color: 'var(--primary-700)'
    },
    messagesArea: {
      flex: 1,
      overflowY: 'auto',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    emptyState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      textAlign: 'center'
    },
    emptyIcon: {
      width: '4rem',
      height: '4rem',
      color: 'var(--gray-300)',
      marginBottom: '1rem'
    },
    emptyTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: 'var(--gray-700)',
      marginBottom: '0.5rem'
    },
    emptyText: {
      fontSize: '0.875rem',
      color: 'var(--gray-500)',
      marginBottom: '1.5rem'
    },
    suggestedPrompts: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      width: '100%'
    },
    promptButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      backgroundColor: isDark ? '#374151' : 'white',
      border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.2s',
      fontSize: '0.875rem',
      color: isDark ? '#e5e7eb' : 'var(--gray-700)'
    },
    message: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    messageUser: {
      alignItems: 'flex-end'
    },
    messageAssistant: {
      alignItems: 'flex-start'
    },
    messageBubble: {
      maxWidth: '85%',
      padding: '0.75rem 1rem',
      borderRadius: '1rem',
      fontSize: '0.875rem',
      lineHeight: '1.5'
    },
    userBubble: {
      backgroundColor: 'var(--primary-600)',
      color: 'white',
      borderBottomRightRadius: '0.25rem'
    },
    assistantBubble: {
      backgroundColor: 'var(--gray-100)',
      color: 'var(--gray-900)',
      borderBottomLeftRadius: '0.25rem'
    },
    streamingBubble: {
      backgroundColor: 'var(--gray-100)',
      color: 'var(--gray-900)',
      borderBottomLeftRadius: '0.25rem',
      position: 'relative'
    },
    typingIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.5rem 0'
    },
    typingDot: {
      width: '0.5rem',
      height: '0.5rem',
      backgroundColor: 'var(--gray-400)',
      borderRadius: '50%',
      animation: 'typing 1.4s infinite ease-in-out'
    },
    inputArea: {
      padding: '1rem',
      borderTop: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
      backgroundColor: isDark ? '#111827' : 'var(--gray-50)'
    },
    inputWrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '0.75rem',
      backgroundColor: isDark ? '#1f2937' : 'white',
      borderRadius: '1rem',
      border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
      padding: '0.5rem 0.75rem',
      transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    inputWrapperFocused: {
      borderColor: 'var(--primary-500)',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    textarea: {
      flex: 1,
      border: 'none',
      outline: 'none',
      resize: 'none',
      fontSize: '0.875rem',
      lineHeight: '1.5',
      maxHeight: '120px',
      padding: '0.375rem 0',
      backgroundColor: 'transparent',
      fontFamily: 'inherit',
      color: isDark ? '#f9fafb' : 'inherit'
    },
    sendButton: {
      padding: '0.5rem',
      backgroundColor: 'var(--primary-600)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      flexShrink: 0
    },
    sendButtonDisabled: {
      backgroundColor: 'var(--gray-300)',
      cursor: 'not-allowed'
    },
    stopButton: {
      padding: '0.5rem',
      backgroundColor: 'var(--red-500)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    keyboardHint: {
      fontSize: '0.6875rem',
      color: 'var(--gray-400)',
      marginTop: '0.5rem',
      textAlign: 'center'
    },
    errorBanner: {
      padding: '0.75rem 1rem',
      backgroundColor: 'var(--red-50)',
      borderBottom: '1px solid var(--red-100)',
      color: 'var(--red-700)',
      fontSize: '0.8125rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    markdown: {
      '& p': { margin: '0.5rem 0' },
      '& p:first-child': { marginTop: 0 },
      '& p:last-child': { marginBottom: 0 },
      '& code': {
        backgroundColor: 'rgba(0,0,0,0.1)',
        padding: '0.125rem 0.375rem',
        borderRadius: '0.25rem',
        fontSize: '0.8125rem'
      },
      '& pre': {
        backgroundColor: 'var(--gray-800)',
        color: 'var(--gray-100)',
        padding: '0.75rem',
        borderRadius: '0.5rem',
        overflow: 'auto',
        fontSize: '0.8125rem',
        margin: '0.5rem 0'
      },
      '& ul, & ol': {
        paddingLeft: '1.25rem',
        margin: '0.5rem 0'
      },
      '& li': {
        margin: '0.25rem 0'
      }
    }
  };

  // Add keyframe animation for typing dots
  const styleSheet = document.styleSheets[0];
  const typingAnimation = `
    @keyframes typing {
      0%, 60%, 100% { transform: translateY(0); }
      30% { transform: translateY(-0.25rem); }
    }
  `;
  try {
    if (!document.querySelector('#typing-animation')) {
      const style = document.createElement('style');
      style.id = 'typing-animation';
      style.textContent = typingAnimation;
      document.head.appendChild(style);
    }
  } catch (e) {
    // Ignore
  }

  return (
    <>
      {/* Overlay */}
      <div style={styles.overlay} onClick={closePanel} />
      
      {/* Panel */}
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <SparklesIcon style={styles.headerIcon} />
            <h3 style={styles.headerTitle}>AI Assistant</h3>
          </div>
          <div style={styles.headerActions}>
            {/* New Conversation */}
            <button
              style={styles.iconButton}
              onClick={handleNewConversation}
              title="New conversation"
            >
              <PlusIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>
            
            {/* History Dropdown */}
            <div style={styles.historyDropdown} ref={historyRef}>
              <button
                style={styles.iconButton}
                onClick={() => setShowHistory(!showHistory)}
                title="Conversation history"
              >
                <ChatBubbleLeftRightIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                <ChevronDownIcon style={{ width: '0.875rem', height: '0.875rem', marginLeft: '0.125rem' }} />
              </button>
              
              {showHistory && (
                <div style={styles.historyMenu}>
                  <div style={styles.historyHeader}>
                    <span style={styles.historyTitle}>Recent Chats</span>
                    <button style={styles.newChatButton} onClick={handleNewConversation}>
                      <PlusIcon style={{ width: '0.875rem', height: '0.875rem' }} />
                      New
                    </button>
                  </div>
                  {conversations.length === 0 ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                      No conversations yet
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <div
                        key={conv.uuid}
                        style={{
                          ...styles.historyItem,
                          ...(currentConversation?.uuid === conv.uuid ? styles.historyItemActive : {})
                        }}
                        onClick={() => handleSelectConversation(conv)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentConversation?.uuid === conv.uuid ? 'var(--primary-50)' : 'transparent'}
                      >
                        <div>
                          <div style={styles.historyItemTitle}>{conv.title || 'New Conversation'}</div>
                          <div style={styles.historyItemDate}>{formatDate(conv.created_at)}</div>
                        </div>
                        <button
                          style={styles.deleteButton}
                          onClick={(e) => handleDeleteConversation(e, conv)}
                          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--red-500)'}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--gray-400)'}
                        >
                          <TrashIcon style={{ width: '1rem', height: '1rem' }} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Close */}
            <button
              style={styles.iconButton}
              onClick={closePanel}
              title="Close (Esc)"
            >
              <XMarkIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div style={styles.errorBanner}>
            <span>{error}</span>
            <button
              onClick={clearError}
              style={{ background: 'none', border: 'none', color: 'var(--red-600)', cursor: 'pointer', padding: '0.25rem' }}
            >
              <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
            </button>
          </div>
        )}

        {/* Context Banner */}
        {context && (
          <div style={styles.contextBanner}>
            <SparklesIcon style={{ width: '1rem', height: '1rem' }} />
            <span>
              Context: <strong>{context.name || context.type}</strong>
            </span>
          </div>
        )}

        {/* Messages Area */}
        <div style={styles.messagesArea}>
          {messages.length === 0 && !isStreaming ? (
            <div style={styles.emptyState}>
              <SparklesIcon style={styles.emptyIcon} />
              <h4 style={styles.emptyTitle}>How can I help you today?</h4>
              <p style={styles.emptyText}>
                Ask me anything about your marketing campaigns, content, or let me help you create something new.
              </p>
              <div style={styles.suggestedPrompts}>
                {suggestedPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    style={styles.promptButton}
                    onClick={() => {
                      setInputValue(prompt.prompt);
                      inputRef.current?.focus();
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary-300)';
                      e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : 'var(--primary-50)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = isDark ? '#4b5563' : 'var(--gray-200)';
                      e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'white';
                    }}
                  >
                    <prompt.icon style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary-600)' }} />
                    {prompt.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    ...styles.message,
                    ...(message.role === 'user' ? styles.messageUser : styles.messageAssistant)
                  }}
                >
                  <div
                    style={{
                      ...styles.messageBubble,
                      ...(message.role === 'user' ? styles.userBubble : styles.assistantBubble)
                    }}
                  >
                    {message.role === 'user' ? (
                      message.content
                    ) : (
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Streaming message */}
              {isStreaming && streamingMessage && (
                <div style={{ ...styles.message, ...styles.messageAssistant }}>
                  <div style={{ ...styles.messageBubble, ...styles.streamingBubble }}>
                    <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                  </div>
                </div>
              )}
              
              {/* Loading indicator */}
              {(isLoading || (isStreaming && !streamingMessage)) && (
                <div style={{ ...styles.message, ...styles.messageAssistant }}>
                  <div style={{ ...styles.messageBubble, ...styles.assistantBubble }}>
                    <div style={styles.typingIndicator}>
                      <div style={{ ...styles.typingDot, animationDelay: '0s' }} />
                      <div style={{ ...styles.typingDot, animationDelay: '0.2s' }} />
                      <div style={{ ...styles.typingDot, animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Generated Tasks Display */}
              {generatedTasks && generatedTasks.length > 0 && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '0.75rem',
                  marginTop: '0.5rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--gray-700)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <ClipboardDocumentListIcon style={{ width: '1.125rem', height: '1.125rem' }} />
                      Generated Tasks ({generatedTasks.length})
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        style={{
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.75rem',
                          backgroundColor: isDark ? '#374151' : 'white',
                          border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          color: isDark ? '#d1d5db' : 'var(--gray-600)'
                        }}
                        onClick={clearGeneratedContent}
                      >
                        Clear
                      </button>
                      <button
                        style={{
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.75rem',
                          backgroundColor: 'var(--primary-600)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => saveTasks(generatedTasks)}
                      >
                        Save All
                      </button>
                    </div>
                  </div>
                  {generatedTasks.map((task, index) => (
                    <TaskPreviewCard
                      key={index}
                      task={task}
                      compact={true}
                      onSave={(t) => saveTasks([t])}
                      onDelete={(t) => {
                        const newTasks = generatedTasks.filter((_, i) => i !== index);
                        if (newTasks.length === 0) {
                          clearGeneratedContent();
                        }
                      }}
                    />
                  ))}
                </div>
              )}
              
              {/* Generated Plan Display */}
              {generatedPlan && (
                <div style={{ marginTop: '0.5rem' }}>
                  <PlanPreviewCard
                    plan={generatedPlan}
                    onSave={savePlan}
                    onTaskSave={(t) => saveTasks([t])}
                  />
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Action Buttons for Task/Plan Generation */}
        {messages.length > 0 && !isLoading && !isStreaming && (
          <div style={{
            padding: '0.5rem 1rem',
            borderTop: `1px solid ${isDark ? '#374151' : 'var(--gray-100)'}`,
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'center',
            backgroundColor: isDark ? '#111827' : 'var(--gray-50)'
          }}>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 0.875rem',
                fontSize: '0.8125rem',
                fontWeight: '500',
                backgroundColor: isDark ? '#374151' : 'white',
                border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                borderRadius: '0.5rem',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                color: isDark ? '#e5e7eb' : 'var(--gray-700)',
                transition: 'all 0.2s'
              }}
              onClick={generateTasksFromConversation}
              disabled={isGenerating}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.borderColor = 'var(--primary-300)';
                  e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : 'var(--primary-50)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDark ? '#4b5563' : 'var(--gray-300)';
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'white';
              }}
            >
              <LightBulbIcon style={{ width: '1rem', height: '1rem' }} />
              {isGenerating ? 'Generating...' : 'Extract Tasks'}
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 0.875rem',
                fontSize: '0.8125rem',
                fontWeight: '500',
                backgroundColor: isDark ? '#374151' : 'white',
                border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                borderRadius: '0.5rem',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                color: isDark ? '#e5e7eb' : 'var(--gray-700)',
                transition: 'all 0.2s'
              }}
              onClick={() => generatePlanFromConversation()}
              disabled={isGenerating}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.borderColor = 'var(--primary-300)';
                  e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : 'var(--primary-50)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDark ? '#4b5563' : 'var(--gray-300)';
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'white';
              }}
            >
              <RocketLaunchIcon style={{ width: '1rem', height: '1rem' }} />
              {isGenerating ? 'Generating...' : 'Generate Plan'}
            </button>
          </div>
        )}

        {/* Input Area */}
        <div style={styles.inputArea}>
          <div
            style={styles.inputWrapper}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary-500)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--gray-300)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <textarea
              ref={inputRef}
              style={styles.textarea}
              placeholder="Ask me anything..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            {isStreaming ? (
              <button
                style={styles.stopButton}
                onClick={stopStreaming}
                title="Stop generating"
              >
                <StopIcon style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            ) : (
              <button
                style={{
                  ...styles.sendButton,
                  ...((!inputValue.trim() || isLoading) ? styles.sendButtonDisabled : {})
                }}
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                title="Send message"
              >
                <PaperAirplaneIcon style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            )}
          </div>
          <div style={styles.keyboardHint}>
            Press Enter to send • Ctrl+Shift+A to toggle
          </div>
        </div>
      </div>
    </>
  );
};

export default AIChatPanel;
